import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { WhiteLabelService } from '@flexyoo/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CountryCode } from 'libphonenumber-js';
import {
  buildPhoneCountryOptions,
  DEFAULT_PHONE_COUNTRY,
  formatPhoneNationalInput,
  formatPhoneNumberNationalForDisplay,
  getCursorPositionForFormattedPhone,
  getFlagUrl,
  getPhoneCallingCode,
  isPhoneNumberValid,
  normalizePhoneDigits,
  parsePastedPhoneValue,
  parseSavedPhoneNumber,
  toE164PhoneNumber,
} from './utils';

@Component({
  selector: 'app-mobility-budget-phone-input',
  templateUrl: './mobility-budget-phone-input.component.html',
  styleUrl: './mobility-budget-phone-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [TranslateModule, MatMenuModule, MatIcon],
})
export class MobilityBudgetPhoneInputComponent {
  private readonly translate = inject(TranslateService);
  protected readonly wls = inject(WhiteLabelService);
  private readonly phoneInput = viewChild<ElementRef<HTMLInputElement>>('phoneInput');

  readonly savedPhoneNumber = input<string | null>(null);
  readonly isConfirmed = input(false);
  readonly showError = input(false);

  readonly savedPhoneParsed = output<string>();
  readonly valueChanged = output<void>();

  protected readonly selectedCountry = signal<CountryCode>(DEFAULT_PHONE_COUNTRY);
  private readonly phoneNationalDigits = signal('');
  private readonly wasSavedPhoneApplied = signal(false);

  protected readonly countryOptions = computed(() =>
    buildPhoneCountryOptions(this.translate.currentLang || 'en')
  );

  protected readonly selectedCallingCode = computed(() =>
    getPhoneCallingCode(this.selectedCountry())
  );

  protected readonly hasPhoneValue = computed(() => this.phoneNationalDigits().length > 0);

  protected readonly getFlagUrl = getFlagUrl;

  readonly isValid = computed(() =>
    isPhoneNumberValid(this.selectedCountry(), this.phoneNationalDigits())
  );

  constructor() {
    effect(() => {
      const savedPhone = this.savedPhoneNumber();

      if (!savedPhone?.trim() || this.wasSavedPhoneApplied() || this.isConfirmed()) {
        return;
      }

      this.applySavedPhoneNumber(savedPhone);
    });
  }

  getE164(): string | null {
    return toE164PhoneNumber(this.selectedCountry(), this.phoneNationalDigits());
  }

  setNationalDisplayValue(value: string): void {
    this.setPhoneInputDisplayValue(value);
  }

  protected onCountryChange(country: CountryCode): void {
    if (this.isConfirmed()) {
      return;
    }

    this.selectedCountry.set(country);
    this.resetPhoneInput();
    this.valueChanged.emit();
  }

  protected onPhoneInput(event: Event): void {
    if (this.isConfirmed()) {
      return;
    }

    const input = event.target as HTMLInputElement;
    const selectionStart = input.selectionStart ?? input.value.length;
    const selectionEnd = input.selectionEnd ?? selectionStart;
    const isTypingAtEnd =
      selectionStart === input.value.length && selectionEnd === input.value.length;
    const digitsBeforeCursor = normalizePhoneDigits(input.value.slice(0, selectionStart)).length;
    const rawDigits = normalizePhoneDigits(input.value);
    const { formatted, digits } = formatPhoneNationalInput(this.selectedCountry(), rawDigits);

    this.phoneNationalDigits.set(digits);

    if (input.value === formatted) {
      this.valueChanged.emit();
      return;
    }

    input.value = formatted;

    const cursorPosition = isTypingAtEnd
      ? formatted.length
      : getCursorPositionForFormattedPhone(formatted, digitsBeforeCursor);

    input.setSelectionRange(cursorPosition, cursorPosition);
    this.valueChanged.emit();
  }

  protected onPhonePaste(event: ClipboardEvent): void {
    if (this.isConfirmed()) {
      return;
    }

    event.preventDefault();

    const input = event.target as HTMLInputElement;
    const pastedText = event.clipboardData?.getData('text') ?? '';
    const selectionStart = input.selectionStart ?? input.value.length;
    const selectionEnd = input.selectionEnd ?? selectionStart;
    const mergedValue =
      input.value.slice(0, selectionStart) + pastedText + input.value.slice(selectionEnd);
    const { country, digits } = parsePastedPhoneValue(this.selectedCountry(), mergedValue);

    if (country !== this.selectedCountry()) {
      this.selectedCountry.set(country);
    }

    const { formatted } = formatPhoneNationalInput(country, digits);

    this.phoneNationalDigits.set(digits);
    input.value = formatted;
    input.setSelectionRange(formatted.length, formatted.length);
    this.valueChanged.emit();
  }

  protected clearPhone(): void {
    if (this.isConfirmed()) {
      return;
    }

    this.resetPhoneInput();
    this.valueChanged.emit();
  }

  private applySavedPhoneNumber(savedPhone: string): void {
    const parsed = parseSavedPhoneNumber(savedPhone);

    if (!parsed) {
      return;
    }

    this.selectedCountry.set(parsed.country);
    this.phoneNationalDigits.set(parsed.digits);
    this.wasSavedPhoneApplied.set(true);
    this.setPhoneInputDisplayValue(formatPhoneNumberNationalForDisplay(parsed.e164));
    this.savedPhoneParsed.emit(parsed.e164);
  }

  private setPhoneInputDisplayValue(value: string): void {
    const applyValue = () => {
      const input = this.phoneInput()?.nativeElement;

      if (input) {
        input.value = value;
      }
    };

    applyValue();
    setTimeout(applyValue);
  }

  private resetPhoneInput(): void {
    this.phoneNationalDigits.set('');

    const input = this.phoneInput()?.nativeElement;

    if (input) {
      input.value = '';
    }
  }
}
